<template>
  <div class="tab-content-container">
    <div class="content-wrapper">
      <!-- 年別コンテンツ表示エリア -->
      <div v-if="selectedYear" class="year-content">
        <!-- コンテンツエリア -->
        <div class="content-area">
          <!-- 空の状態を表示 -->
          <div v-if="!hasContent" class="empty-state">
            <YearEmptyState :year="selectedYear" />
          </div>
          
          <!-- 実際のコンテンツ -->
          <div v-else class="actual-content">
            <!-- 年別サマリーカード -->
            <div class="summary-card-wrapper">
              <YearSummaryCard 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
            
            <!-- シェアボタン（1つ目） -->
            <div class="share-button-wrapper">
              <ShareButton 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
            
            <!-- 受け取ったギフトサマリー -->
            <div v-if="hasReceivedGifts" class="gift-summary-wrapper">
              <ReceivedGiftsSummary 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
            
            <!-- 盛り上がった配信 -->
            <div v-if="hasBroadcastRecords" class="popular-streams-wrapper">
              <PopularStreams 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
            
            <!-- 主に視聴したユーザー -->
            <div v-if="hasViewRecords" class="popular-viewers-wrapper">
              <PopularViewers 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
            
            <!-- 送ったギフトサマリー -->
            <div v-if="hasSentGifts" class="sent-gifts-wrapper">
              <SentGiftsSummary 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
            
            <!-- シェアボタン（2つ目） -->
            <div class="share-button-wrapper">
              <ShareButton 
                :year="selectedYear"
                :year-data="yearData"
                :user="user"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import YearSummaryCard from './YearSummaryCard.vue'
import ShareButton from './ShareButton.vue'
import YearEmptyState from './YearEmptyState.vue'
import ReceivedGiftsSummary from './ReceivedGiftsSummary.vue'
import SentGiftsSummary from './SentGiftsSummary.vue'
import PopularBroadcasts from './PopularBroadcasts.vue'
import PopularUsers from './PopularUsers.vue'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { HistoryAlbumUser } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumUser'

@Component({
  components: {
    YearSummaryCard,
    ShareButton,
    YearEmptyState,
    ReceivedGiftsSummary,
    SentGiftsSummary,
    PopularStreams: PopularBroadcasts,
    PopularViewers: PopularUsers
  }
})
export default class TabContent extends Vue {
  // 選択された年をpropsで受け取る
  @Prop({ type: Number, required: true })
  selectedYear!: number

  // 年別データをpropsで受け取る
  @Prop({ type: Object, default: null })
  yearData!: HistoryAlbumKeyedData | null

  // ユーザー情報をpropsで受け取る
  @Prop({ type: Object, default: null })
  user!: HistoryAlbumUser | null

  // コンテンツがあるかどうか（活動があったかどうかで判定）
  get hasContent(): boolean {
    if (!this.yearData) {
      return false
    }
    
    // 配信時間と視聴時間の両方が0の場合は活動がなかったとみなす
    const viewHours = this.yearData.myRecord?.totalBroadcastViewHour || 0
    const broadcastHours = this.yearData.myRecord?.totalBroadcastHour || 0
    
    return viewHours > 0 || broadcastHours > 0
  }

  // 受け取ったギフトがあるかどうか
  get hasReceivedGifts(): boolean {
    return !!(this.yearData?.receiveGiftRecords && this.yearData.receiveGiftRecords.length > 0)
  }

  // 配信記録があるかどうか
  get hasBroadcastRecords(): boolean {
    return !!(this.yearData?.broadcastRecords && this.yearData.broadcastRecords.length > 0)
  }

  // 視聴記録があるかどうか
  get hasViewRecords(): boolean {
    return !!(this.yearData?.broadcastViewRecords && this.yearData.broadcastViewRecords.length > 0)
  }

  // 送ったギフトがあるかどうか
  get hasSentGifts(): boolean {
    return !!(this.yearData?.sendGiftRecords && this.yearData.sendGiftRecords.length > 0)
  }
}
</script>

<style scoped>
.tab-content-container {
  position: relative;
  width: 100%;
  min-height: 300px;
  padding: 0;
}

.content-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

/* 年コンテンツ */
.year-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

/* コンテンツエリア */
.content-area {
  position: relative;
  width: 100%;
  min-height: 200px;
  padding: 0;
}

/* 空の状態 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 200px;
  text-align: center;
}


/* 実際のコンテンツ */
.actual-content {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0 16px;
}

.summary-card-wrapper {
  position: relative;
  width: 100%;
}

.gift-summary-wrapper {
  position: relative;
  width: 100%;
}

.sent-gifts-wrapper {
  position: relative;
  width: 100%;
}

.popular-streams-wrapper {
  position: relative;
  width: 100%;
}

.popular-viewers-wrapper {
  position: relative;
  width: 100%;
}

.share-button-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
